import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { MEDIA, TYPE_LABELS } from "@/mocks/media";
import { NEWS, NEWS_CATEGORY_LABELS } from "@/mocks/news";
import { usePageMeta } from "@/hooks/usePageMeta";
import Breadcrumb from "@/components/base/Breadcrumb";
import MediaCard from "@/components/base/MediaCard";
import EmptyState from "@/components/base/EmptyState";

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryParam = searchParams.get("query") ?? "";
  const [input, setInput] = useState(queryParam);

  useEffect(() => {
    setInput(queryParam);
  }, [queryParam]);

  const keyword = queryParam.trim().toLowerCase();

  usePageMeta({
    title: keyword ? `“${queryParam}” 的搜索结果 - WWW.17C影视平台` : "搜索 - WWW.17C影视平台",
    description: `在 WWW.17C 搜索影视作品与资讯，关键词：${queryParam || "全部"}。`,
    keywords: `${queryParam},影视搜索,影视资讯搜索,WWW.17C`,
  });

  const mediaResults = useMemo(() => {
    if (!keyword) return [];
    return MEDIA.filter((item) =>
      [item.title, item.summary, item.director, item.region, item.plot, ...item.genres, ...item.tags, ...item.cast]
        .join(" ")
        .toLowerCase()
        .includes(keyword),
    );
  }, [keyword]);

  const newsResults = useMemo(() => {
    if (!keyword) return [];
    return NEWS.filter((item) =>
      [item.title, item.summary, item.source, ...item.content].join(" ").toLowerCase().includes(keyword),
    );
  }, [keyword]);

  const total = mediaResults.length + newsResults.length;

  const hotMedia = useMemo(() => [...MEDIA].sort((a, b) => b.views - a.views).slice(0, 6), []);
  const hotNews = useMemo(() => NEWS.slice(0, 3), []);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const value = input.trim();
    setSearchParams(value ? { query: value } : {});
  };

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-6 md:px-8 md:py-8">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索" }]} />

      <div className="mt-4 rounded-lg bg-primary-950 px-6 py-8 md:px-10">
        <h1 className="font-heading text-2xl font-extrabold text-background-50 md:text-3xl">全站搜索</h1>
        <form onSubmit={handleSubmit} className="mt-5 flex max-w-2xl flex-col gap-3 sm:flex-row">
          <div className="flex h-11 flex-1 items-center gap-2 rounded-full bg-background-50 px-4">
            <i className="ri-search-line text-foreground-400"></i>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="输入影视名称、类型或资讯关键词"
              className="h-full w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              autoFocus
            />
          </div>
          <button
            type="submit"
            className="flex h-11 items-center justify-center gap-1.5 whitespace-nowrap rounded-full bg-accent-500 px-7 text-sm font-semibold text-primary-950 transition-colors hover:bg-accent-400"
          >
            <i className="ri-search-line"></i>
            搜索
          </button>
        </form>
      </div>

      {keyword && (
        <p className="mt-6 text-sm text-foreground-500">
          关键词 <span className="font-semibold text-foreground-900">“{queryParam}”</span> 共找到{" "}
          <span className="font-semibold text-accent-600">{total}</span> 条结果
        </p>
      )}

      {/* 有结果 */}
      {keyword && total > 0 && (
        <div className="mt-8 space-y-12">
          {mediaResults.length > 0 && (
            <section>
              <h2 className="mb-5 flex items-center gap-2.5 font-heading text-xl font-bold text-foreground-950">
                <span className="h-5 w-1.5 rounded-full bg-accent-500"></span>
                影视作品
                <span className="text-sm font-normal text-foreground-500">（{mediaResults.length}）</span>
              </h2>
              <div className="grid grid-cols-2 gap-x-4 gap-y-6 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
                {mediaResults.map((item) => (
                  <MediaCard key={item.id} item={item} />
                ))}
              </div>
            </section>
          )}

          {newsResults.length > 0 && (
            <section>
              <h2 className="mb-5 flex items-center gap-2.5 font-heading text-xl font-bold text-foreground-950">
                <span className="h-5 w-1.5 rounded-full bg-accent-500"></span>
                相关资讯
                <span className="text-sm font-normal text-foreground-500">（{newsResults.length}）</span>
              </h2>
              <div className="divide-y divide-background-200 rounded-lg border border-background-200 bg-background-50">
                {newsResults.map((item) => (
                  <Link
                    key={item.id}
                    to={`/news/${item.id}`}
                    className="group flex flex-col gap-1.5 p-4 transition-colors hover:bg-background-100"
                  >
                    <div className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
                      <span className="rounded-full bg-secondary-50 px-2.5 py-0.5 font-medium text-secondary-700">
                        {NEWS_CATEGORY_LABELS[item.category]}
                      </span>
                      <span>{item.publishedAt}</span>
                      <span>{item.source}</span>
                    </div>
                    <h3 className="font-heading text-sm font-semibold text-foreground-900 transition-colors group-hover:text-primary-600 md:text-base">
                      {item.title}
                    </h3>
                    <p className="line-clamp-1 text-sm text-foreground-600">{item.summary}</p>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      )}

      {/* 无结果 */}
      {keyword && total === 0 && (
        <div className="mt-8 space-y-10">
          <EmptyState
            icon="ri-emotion-sad-line"
            title="没有找到相关内容"
            description={`没有与“${queryParam}”匹配的影视或资讯，试试换个关键词吧。`}
          />

          <section>
            <h2 className="mb-5 font-heading text-lg font-bold text-foreground-950">为你推荐热门影视</h2>
            <div className="grid grid-cols-2 gap-x-4 gap-y-6 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
              {hotMedia.map((item) => (
                <MediaCard key={item.id} item={item} />
              ))}
            </div>
          </section>

          <section>
            <h2 className="mb-5 font-heading text-lg font-bold text-foreground-950">热门资讯</h2>
            <div className="divide-y divide-background-200 rounded-lg border border-background-200 bg-background-50">
              {hotNews.map((item) => (
                <Link
                  key={item.id}
                  to={`/news/${item.id}`}
                  className="group flex items-center justify-between gap-4 p-4 transition-colors hover:bg-background-100"
                >
                  <h3 className="line-clamp-1 font-heading text-sm font-semibold text-foreground-900 transition-colors group-hover:text-primary-600">
                    {item.title}
                  </h3>
                  <span className="shrink-0 text-xs text-foreground-500">{item.publishedAt}</span>
                </Link>
              ))}
            </div>
          </section>
        </div>
      )}

      {/* 未输入 */}
      {!keyword && (
        <div className="mt-8">
          <EmptyState
            icon="ri-search-line"
            title="输入关键词开始搜索"
            description="可以搜索影视名称、类型标签或资讯标题，例如「悬疑」「治愈」「古装」。"
          />
          <div className="mt-8 flex flex-wrap gap-2">
            {["悬疑", "古装", "科幻", "治愈", "综艺", "犯罪"].map((tag) => (
              <button
                key={tag}
                type="button"
                onClick={() => setSearchParams({ query: tag })}
                className="rounded-full border border-background-300 px-4 py-1.5 text-sm text-foreground-700 transition-colors hover:border-accent-400 hover:text-accent-600"
              >
                {tag}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* 分类入口 */}
      <div className="mt-12 flex flex-wrap gap-3">
        {(["movie", "series", "variety"] as const).map((t) => (
          <Link
            key={t}
            to={`/category/${t}`}
            className="flex items-center gap-1.5 rounded-md border border-background-300 px-4 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600"
          >
            <i className="ri-arrow-right-up-line"></i>
            浏览{TYPE_LABELS[t]}
          </Link>
        ))}
      </div>
    </div>
  );
}